import type { WorkScheduleDayEntry } from '@functieloonhuis/shared';
import { getAriaLabel } from '../utils/weekUtils';

interface ScheduleDayCellProps {
  entry: WorkScheduleDayEntry | undefined;
  dayOfWeek: number;
}

export function ScheduleDayCell({ entry, dayOfWeek }: ScheduleDayCellProps) {
  const ariaLabel = getAriaLabel(entry, dayOfWeek);

  // Free / no entry
  if (!entry || !entry.isWorkday) {
    return (
      <div
        className="flex flex-col items-center justify-center p-3 rounded-lg bg-gray-50 border border-gray-200 min-h-[80px]"
        role="gridcell"
        aria-label={ariaLabel}
      >
        <svg className="h-5 w-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
        </svg>
        <span className="text-xs text-gray-400 mt-1">Vrij</span>
      </div>
    );
  }

  const isKantoor = entry.location === 'kantoor';
  const bgClass = isKantoor ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200';
  const textClass = isKantoor ? 'text-green-700' : 'text-blue-700';
  const iconClass = isKantoor ? 'text-green-500' : 'text-blue-500';

  return (
    <div
      className={`flex flex-col items-center justify-center p-3 rounded-lg border min-h-[80px] ${bgClass}`}
      role="gridcell"
      aria-label={ariaLabel}
    >
      {/* Location icon */}
      {isKantoor ? (
        <svg className={`h-5 w-5 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ) : (
        <svg className={`h-5 w-5 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )}

      {/* Location label */}
      <span className={`text-xs font-medium mt-1 ${textClass}`}>
        {isKantoor ? 'Kantoor' : 'Thuis'}
      </span>

      {/* Half day indicator */}
      {entry.isHalfDay && entry.halfDayPeriod && (
        <span className={`text-[10px] mt-0.5 ${textClass} opacity-75`}>
          {entry.halfDayPeriod === 'ochtend' ? 'Ochtend' : 'Middag'}
        </span>
      )}

      {/* Times */}
      {entry.startTime && entry.endTime && (
        <span className={`text-[10px] mt-0.5 ${textClass} opacity-60`}>
          {entry.startTime}-{entry.endTime}
        </span>
      )}
    </div>
  );
}
